import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Archive, CheckCircle2, RotateCw, X } from 'lucide-react';

import { listEmployees } from '@/api/employees.api';
import { archiveTodo, carryOverTodo, completeTodo, updateTodo, type Todo } from '@/api/eos/todos.api';
import { FileAttachments } from '@/components/files/FileAttachments';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SubtaskList } from '@/components/eos/SubtaskList';
import { describeEosError, eosErrorCode } from '@/lib/eos/errors';
import { hasCapability, isSuperadmin } from '@/lib/capabilities';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * Right-side slide-over for a single to-do (ADR-023 Wave 2). View/edit the
 * core fields, manage subtasks (parallel or sequential), and attach files.
 * Mirrors the accountability-chart drawer shell. Every mutation invalidates
 * `['eos','todos']` so the list and this drawer re-render off fresh data — the
 * parent passes the refreshed todo back down by id.
 */

const SELECT_CLS =
  'rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20';

/** Maps the subtask-specific server codes; delegates the rest to the shared describer. */
function describeTodoError(err: unknown, fallback: string): string {
  switch (eosErrorCode(err)) {
    case 'has_incomplete_subtasks':
      return 'Finish every subtask before completing this to-do.';
    case 'subtask_blocked':
      return 'An earlier subtask has to be done first (sequential mode).';
    case 'nested_subtasks_unsupported':
      return 'Subtasks can only be one level deep.';
    default:
      return describeEosError(err, fallback);
  }
}

export function TodoDetailDrawer({ todo, onClose }: { todo: Todo; onClose: () => void }) {
  const { state: authState } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();

  const me = authState.status === 'authenticated' ? authState.me : null;
  const myMembershipId = me?.membership.id ?? null;
  const canCarryAny = me !== null && (isSuperadmin(me) || hasCapability(me, 'run_meetings'));

  // Esc closes.
  useEffect(() => {
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  const employeesQuery = useQuery({
    queryKey: ['employees', 'directory'],
    queryFn: () => listEmployees({ status: 'active', limit: 500 }),
  });

  const members = useMemo(
    () =>
      (employeesQuery.data?.data ?? []).flatMap((e) =>
        e.membershipId !== null
          ? [{ membershipId: e.membershipId, name: `${e.firstName} ${e.lastName}` }]
          : [],
      ),
    [employeesQuery.data],
  );
  // Owner picker: "Myself" plus every other active member (self excluded for
  // cleanliness — it is already the default).
  const otherMembers = useMemo(
    () => members.filter((m) => m.membershipId !== myMembershipId),
    [members, myMembershipId],
  );

  // Local edit state (component is keyed on todo.id in the parent, so these
  // initialise fresh whenever a different to-do opens).
  const [title, setTitle] = useState(todo.title);
  const [description, setDescription] = useState(todo.description ?? '');
  const [ownerMembershipId, setOwnerMembershipId] = useState(todo.ownerMembershipId ?? myMembershipId ?? '');
  const [dueDate, setDueDate] = useState(todo.dueDate.slice(0, 10));

  const dirty =
    title.trim() !== todo.title ||
    (description.trim() || '') !== (todo.description ?? '') ||
    ownerMembershipId !== (todo.ownerMembershipId ?? myMembershipId ?? '') ||
    dueDate !== todo.dueDate.slice(0, 10);

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['eos', 'todos'] });
  }

  const canComplete =
    (me !== null && isSuperadmin(me)) ||
    (myMembershipId !== null &&
      (todo.ownerMembershipId === myMembershipId || todo.createdByMembershipId === myMembershipId));
  const canCarry = canCarryAny || (myMembershipId !== null && todo.ownerMembershipId === myMembershipId);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateTodo(todo.id, {
        title: title.trim(),
        description: description.trim() || null,
        ownerMembershipId: ownerMembershipId || undefined,
        dueDate: `${dueDate}T00:00:00.000Z`,
      }),
    onSuccess: () => {
      invalidate();
      toast.show('Saved', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeTodoError(err, 'Could not save the to-do.'), { variant: 'danger' });
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => completeTodo(todo.id),
    onSuccess: invalidate,
    onError: (err) => {
      toast.show(describeTodoError(err, 'Could not complete the to-do.'), { variant: 'danger' });
    },
  });

  const carryMutation = useMutation({
    mutationFn: () => carryOverTodo(todo.id),
    onSuccess: () => {
      invalidate();
      toast.show('Carried over — due date pushed a week', { variant: 'success' });
    },
    onError: (err) => {
      toast.show(describeTodoError(err, 'Could not carry the to-do over.'), { variant: 'danger' });
    },
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveTodo(todo.id),
    onSuccess: () => {
      invalidate();
      toast.show('To-do archived', { variant: 'success' });
      onClose();
    },
    onError: (err) => {
      toast.show(describeTodoError(err, 'Could not archive the to-do.'), { variant: 'danger' });
    },
  });

  const completed = todo.completedAt !== null;
  const subtaskCount = todo.subtasks?.length ?? 0;
  const doneCount = todo.subtasks?.filter((s) => s.completedAt !== null).length ?? 0;

  return (
    <div className="fixed inset-0 z-40 flex">
      <button
        type="button"
        aria-label="Close drawer"
        onClick={onClose}
        className="flex-1 animate-fade-in bg-black/30"
      />
      <aside
        role="dialog"
        aria-label={`To-do: ${todo.title}`}
        className="ml-auto flex h-full w-full max-w-md animate-slide-up flex-col overflow-y-auto bg-white shadow-kw-lg sm:animate-fade-in"
      >
        <header className="flex items-start justify-between gap-3 border-b border-kw-border p-5">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <h2 className="typo-h3 truncate">To-do</h2>
              {completed ? <Badge variant="success">Done</Badge> : null}
              {subtaskCount > 0 ? (
                <Badge variant="info">
                  ▣ {doneCount}/{subtaskCount}
                </Badge>
              ) : null}
            </div>
            {todo.carryCount > 0 ? (
              <p className="mt-1 text-[12px] text-kw-muted">Carried over ×{todo.carryCount}</p>
            ) : null}
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="rounded p-1 text-kw-muted hover:bg-kw-bg hover:text-kw-shark"
          >
            <X className="size-5" />
          </button>
        </header>

        <div className="flex-1 space-y-6 p-5">
          <section className="space-y-4">
            <Input
              label="Title"
              name="drawer-todo-title"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
              }}
            />

            <label className="flex flex-col gap-1">
              <span className="text-[13px] font-medium text-kw-label">Description</span>
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                }}
                rows={3}
                placeholder="Optional context"
                className="rounded-kw-md border border-kw-border bg-white px-3.5 py-2.5 text-[14px] text-kw-shark placeholder:text-kw-muted focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
              />
            </label>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1">
                <span className="text-[13px] font-medium text-kw-label">Owner</span>
                <select
                  aria-label="Owner"
                  value={ownerMembershipId}
                  onChange={(e) => {
                    setOwnerMembershipId(e.target.value);
                  }}
                  className={SELECT_CLS}
                >
                  {myMembershipId ? <option value={myMembershipId}>Myself</option> : null}
                  {otherMembers.map((m) => (
                    <option key={m.membershipId} value={m.membershipId}>
                      {m.name}
                    </option>
                  ))}
                </select>
              </label>

              <Input
                label="Due date"
                name="drawer-todo-due"
                type="date"
                value={dueDate}
                onChange={(e) => {
                  setDueDate(e.target.value);
                }}
              />
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                size="sm"
                isLoading={saveMutation.isPending}
                disabled={!dirty || !title.trim()}
                onClick={() => {
                  saveMutation.mutate();
                }}
              >
                Save changes
              </Button>
            </div>
          </section>

          <section className="border-t border-kw-border pt-5">
            <SubtaskList parent={todo} members={members} />
          </section>

          <section className="border-t border-kw-border pt-5">
            <h3 className="mb-3 text-[12px] font-medium uppercase tracking-wide text-kw-muted">
              Attachments
            </h3>
            <FileAttachments entityType="eos_todo" entityId={todo.id} />
          </section>
        </div>

        <footer className="flex flex-wrap items-center gap-2 border-t border-kw-border p-4">
          {!completed && canComplete ? (
            <Button
              type="button"
              size="sm"
              isLoading={completeMutation.isPending}
              onClick={() => {
                completeMutation.mutate();
              }}
            >
              <CheckCircle2 className="size-4" />
              Complete
            </Button>
          ) : null}
          {!completed && canCarry ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              isLoading={carryMutation.isPending}
              onClick={() => {
                carryMutation.mutate();
              }}
            >
              <RotateCw className="size-3.5" />
              Carry +7d
            </Button>
          ) : null}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto text-kw-muted hover:text-kw-danger-text"
            isLoading={archiveMutation.isPending}
            onClick={() => {
              archiveMutation.mutate();
            }}
          >
            <Archive className="size-4" />
            Archive
          </Button>
        </footer>
      </aside>
    </div>
  );
}
